import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, OnInit, Signal, computed, effect, inject, signal } from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { takeUntil, take } from 'rxjs';
import { growAnimation } from '@newroom-connect/library/animations';
import { CollectionField, CollectionMetadata, PermissionConditionForm, PermissionConditionRule } from '@newroom-connect/library/interfaces';
import { IFlattenedProperty, PermissionConditionService } from '@newroom-connect/library/services';

import { DestroyableComponent } from '../abstract/destroyable/destroyable.component';
import { InputDatepickerComponent, InputNestedSelectComponent, InputSegmentComponent, InputSelectComponent, InputTextComponent } from '../inputs';

interface PropertyOption {
  name: string;
  type: string;
}

interface RuleOption {
  label: string;
  value: PermissionConditionRule;
}

interface NestedSelectOption {
  label: string;
  value: string;
  children: NestedSelectOption[];
}

@Component({
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    InputTextComponent,
    InputDatepickerComponent,
    InputSegmentComponent,
    InputSelectComponent,
    InputNestedSelectComponent
  ],
  selector: 'nrc-permission-condition-builder',
  templateUrl: './permission-condition-builder.component.html',
  animations: [growAnimation],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PermissionConditionBuilderComponent extends DestroyableComponent implements OnInit {
  @Input() public collectionName = '';
  @Input() public condition!: FormGroup<PermissionConditionForm>;
  @Input() public parentField?: CollectionField;
  @Input() public overflowParentElement!: HTMLElement;

  private readonly permissionConditionService = inject(PermissionConditionService);

  // UI State Signals
  public readonly showOperatorSelector = signal(false);

  // Data Signals
  public readonly fields = signal<CollectionField[]>([]);
  public readonly nestedPropertyOptions = signal<NestedSelectOption[]>([]);
  private readonly selectedPropertyName = signal<string>('');

  // Computed Signals
  public readonly selectedField: Signal<CollectionField | undefined> = computed(() => {
    const propertyName = this.selectedPropertyName();

    if (!propertyName) {
      return undefined;
    }

    // For flattened property names (e.g., "market.slugSuffix"), get the actual nested field
    if (propertyName.includes('.')) {
      // Get flattened properties from service and find the exact match
      const flattenedProperties = this.permissionConditionService.getFlattenedProperties(this.collectionName);
      const matchingProperty = flattenedProperties.find(prop => prop.flattenedName === propertyName);

      return matchingProperty?.field;
    }

    // For direct properties, find from current fields
    return this.fields().find(field => field.name === propertyName);
  });

  public readonly propertyOptions: Signal<PropertyOption[]> = computed(() => {
    const currentFields = this.fields();

    if (!currentFields.length) {
      return [];
    }

    return currentFields
      .filter((field: CollectionField) => {
        if (this.parentField) {
          return field.kind === 'scalar' || (field.kind === 'object' && field.relationName);
        }
        return true;
      })
      .map(field => ({
        name: field.name,
        type: field.type
      }));
  });

  public readonly ruleOptions: Signal<RuleOption[]> = computed(() => {
    const field = this.selectedField();

    if (!field) {
      return [];
    }

    const supportedRules = this.permissionConditionService.getSupportedRules(field);

    return supportedRules.map(rule => ({
      label: this.formatRuleLabel(rule),
      value: rule
    }));
  });

  constructor() {
    super();

    // Setup effects after inputs are initialized
    effect(() => {
      if (!this.collectionName && !this.parentField) {
        return;
      }

      this.loadFields();
    }, { allowSignalWrites: true });
  }

  public ngOnInit(): void {
    // Initialize property change listener if condition is available
    if (this.condition) {
      this.setupPropertyChangeListener();

      // Set initial property name
      this.selectedPropertyName.set(this.condition.value.propertyName ?? '');
    }
  }

  /**
   * Sets up listener for property name changes.
   */
  private setupPropertyChangeListener(): void {
    if (!this.condition?.controls.propertyName) {
      return;
    }

    this.condition.controls.propertyName.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(this.handlePropertyChange.bind(this));
  }

  /**
   * Handles property change events.
   * @param propertyName
   */
  private handlePropertyChange(propertyName: string): void {
    if (!propertyName) {
      return;
    }

    this.selectedPropertyName.set(propertyName);

    // For flattened property names (e.g., "Team.ID"), extract the base field name
    const baseFieldName = propertyName.includes('.') ? propertyName.split('.')[0] : propertyName;
    const field = this.fields().find(f => f.name === baseFieldName);

    // Update form with new field type
    this.permissionConditionService.updateFormForField(this.condition, field);
  }

  /**
   * Loads fields for the collection.
   */
  private loadFields(): void {
    const targetCollection = this.parentField?.relationName || this.collectionName;

    if (!targetCollection) {
      this.fields.set([]);
      return;
    }

    // Get collection metadata
    this.permissionConditionService.getCollectionMetadata(targetCollection)
      .pipe(
        take(1),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: (metadata: CollectionMetadata | null) => {
          this.fields.set(metadata?.fields ?? []);
          this.nestedPropertyOptions.set(this.buildNestedOptions(
            this.permissionConditionService.getFlattenedProperties(targetCollection)
          ));

          // If we have fields and no property name is selected, select the first one
          if (metadata?.fields?.length && !this.selectedPropertyName()) {
            const firstField = metadata.fields[0];

            this.condition.patchValue({ propertyName: firstField.name });
            this.selectedPropertyName.set(firstField.name);
            this.permissionConditionService.updateFormForField(this.condition, firstField);
          }
        },
        error: () => {
          this.fields.set([]);
          this.nestedPropertyOptions.set([]);
        }
      });
  }

  /**
   * Formats a rule enum value into a readable label.
   * @param rule
   *
   * @returns A readable label for the rule.
   */
  private formatRuleLabel(rule: PermissionConditionRule): string {
    return rule.split(/(?=[A-Z])/).join(' ').toLowerCase();
  }

  /**
   * Builds nested select options from flattened properties.
   * @param flattenedProperties
   *
   * @returns Nested select options.
   */
  private buildNestedOptions(flattenedProperties: IFlattenedProperty[]): NestedSelectOption[] {
    const rootOptions: NestedSelectOption[] = [];

    for (const property of flattenedProperties) {
      let currentLevel = rootOptions;

      property.path.forEach((segment, index) => {
        let existing = currentLevel.find(option => option.label === segment);

        if (!existing) {
          existing = { label: segment, value: '', children: [] };
          currentLevel.push(existing);
        }

        if (index === property.path.length - 1) {
          existing.value = property.flattenedName;
        }

        currentLevel = existing.children;
      });
    }

    return rootOptions;
  }

  /**
   * Toggles the operator selector.
   *
   * @param rule
   * @param event
   */
  public onOperatorSelect(rule: PermissionConditionRule, event: MouseEvent): void {
    event.stopPropagation();
    this.condition.patchValue({ rule });
    this.showOperatorSelector.set(false);
  }

  /**
   * Toggles the operator selector.
   *
   * @param event
   */
  public toggleOperatorSelector(event: MouseEvent): void {
    event.stopPropagation();
    this.showOperatorSelector.update(show => !show);
  }
}
